/**
 * 🛡️ [Phase 76 — 2026-06-21] Helpers PURS pour la construction du payload
 * de sauvegarde d'un BT (extraits de `EditerBonTravailDialog.doSave`).
 *
 * Ces helpers garantissent que `dates_assignees=[]` EXPLICITE (intention
 * utilisatrice = décochage volontaire de toutes les cases d'un employé)
 * n'est JAMAIS écrasé par un fallback `dates_chantier`.
 *
 * Bug récurrent réglé ici : la sanitization « absent-locked » ressuscitait
 * silencieusement les employés décochés en faisant
 *   `materialized = dates_chantier - absent_dates`
 * quand `dates_assignees=[]` + au moins une date passée settled.
 *
 * Testé par 12+ tests Jest dans `__tests__/btSavePayloadHelpers.test.js`.
 *
 * Convention partagée avec le helper backend `/app/backend/lib/bt_equipe_normalize.py`.
 */

/**
 * Sanitize la liste `employes_assignes` en retirant les dates « absentes verrouillées »
 * (jour passé du BT où l'employé n'a pas punché).
 *
 * ⚠️ PRÉSERVE `dates_assignees=[]` explicite (intention utilisatrice). Si l'employé
 * a été décoché de toutes les cases, on ne le matérialise JAMAIS sur dates_chantier.
 *
 * @param {Array<{employee_id: string, dates_assignees?: any}>} employes
 * @param {Object<string, string[]>} absentPerEmployee  Map employee_id → dates absent verrouillées
 * @param {string[]} datesChantier  Dates de chantier (fallback legacy)
 * @returns {Array}
 */
export function sanitizeEmployesAssignesAbsentLocked(employes, absentPerEmployee, datesChantier) {
  const absentMap = absentPerEmployee || {};
  const dc = datesChantier || [];
  return (employes || []).map(e => {
    const absent = absentMap[e.employee_id] || [];
    if (absent.length === 0) return e;
    // 🛡️ [Phase 76] PRÉSERVE [] explicite — l'utilisatrice a décoché TOUTES les
    // cases volontairement. NE PAS matérialiser sur dates_chantier - absent
    // (= bug historique de résurrection sur les dates futures).
    if (Array.isArray(e.dates_assignees) && e.dates_assignees.length === 0) {
      return e;
    }
    // Cas explicite avec contenu : filtrer les absents
    if (Array.isArray(e.dates_assignees) && e.dates_assignees.length > 0) {
      return { ...e, dates_assignees: e.dates_assignees.filter(d => !absent.includes(d)) };
    }
    // Cas implicite (legacy, dates_assignees undefined/null) : matérialiser
    const materialized = dc.filter(d => !absent.includes(d));
    return { ...e, dates_assignees: materialized };
  });
}

/**
 * Sanitize `chef_dates_assignees` en retirant les dates absent verrouillées.
 *
 * ⚠️ PRÉSERVE [] explicite (chef volontairement absent partout).
 *
 * @param {any} chefDatesAssignees
 * @param {string[]} chefAbsent  Dates absent verrouillées du chef
 * @param {string[]} datesChantier
 * @returns {any}
 */
export function sanitizeChefDatesAssigneesAbsentLocked(chefDatesAssignees, chefAbsent, datesChantier) {
  const absent = chefAbsent || [];
  const dc = datesChantier || [];
  if (absent.length === 0) return chefDatesAssignees;
  // 🛡️ [Phase 76] PRÉSERVE [] explicite
  if (Array.isArray(chefDatesAssignees) && chefDatesAssignees.length === 0) {
    return chefDatesAssignees;
  }
  if (Array.isArray(chefDatesAssignees) && chefDatesAssignees.length > 0) {
    return chefDatesAssignees.filter(d => !absent.includes(d));
  }
  // Cas implicite : matérialiser
  return dc.filter(d => !absent.includes(d));
}

/**
 * 🛡️ [Phase 76] Filet final avant axios.put — détecte toute expansion silencieuse
 * où le payload final écrase un `dates_assignees=[]` explicite de l'état du formulaire.
 *
 * Retourne :
 *   - `payload` corrigé (les expansions silencieuses sont remises à [])
 *   - `expansions` : liste des employees qui ont été ressuscités (log diagnostic)
 *
 * @param {Array} payloadEmployes  Le payload sur le point d'être envoyé
 * @param {Array} formEmployes  L'état actuel du formulaire (form.employes_assignes)
 * @returns {{ payload: Array, expansions: Array<{employee_id: string, expanded_to: string[]}> }}
 */
export function detecterEtCorrigerExpansionsSilencieuses(payloadEmployes, formEmployes) {
  const formMap = new Map(
    (formEmployes || []).map(e => [e.employee_id, e])
  );
  const expansions = [];
  const corrected = (payloadEmployes || []).map(e => {
    const formEntry = formMap.get(e.employee_id);
    if (!formEntry) return e;
    const formDates = formEntry.dates_assignees;
    const payloadDates = e.dates_assignees;
    // Cas critique : form dit [] explicite, mais payload dit non-vide
    if (
      Array.isArray(formDates) && formDates.length === 0 &&
      Array.isArray(payloadDates) && payloadDates.length > 0
    ) {
      expansions.push({ employee_id: e.employee_id, expanded_to: [...payloadDates] });
      // Force [] (intention utilisatrice respectée)
      return { ...e, dates_assignees: [] };
    }
    return e;
  });
  return { payload: corrected, expansions };
}

/**
 * 🛡️ [v13.62 — 2026-02-28] Retire les champs `heures_budgetees_*` du payload
 * quand ils sont `null` (masqués Phase 81 côté serveur pour les non-owners).
 *
 * Contexte : le GET `/crm/bons-travail/{id}` applique `apply_heures_visibility`
 * qui remplace `heures_budgetees` / `heures_budgetees_proposees` /
 * `heures_budgetees_officielles` par `null` pour un `chef_equipe` ou tout autre
 * rôle non-owner + non-CP + non-adjoint. Si le frontend fait un PUT en spread
 * `{...bon, ...overrides}` sans neutraliser ces `null`, Pydantic échoue avec
 * une 422 sur `heures_budgetees: float` (non-Optional).
 *
 * Le backend ignore de toute façon ces champs pour les non-owners
 * (`crm_bons_travail.py` L2594 `bon_dict.pop(forbidden_key)`), donc les
 * retirer côté frontend est strictement équivalent en sémantique.
 *
 * ⚠️ NE retire QUE si la valeur est `null` (masquée). Si l'owner a une
 * vraie valeur numérique, on la laisse passer.
 *
 * @param {Object} payload
 * @returns {Object} Nouveau payload avec les champs masqués retirés
 */
export function stripMaskedHeuresBudgeteesFields(payload) {
  if (!payload || typeof payload !== 'object') return payload;
  const MASKED_FIELDS = [
    'heures_budgetees',
    'heures_budgetees_proposees',
    'heures_budgetees_officielles',
    'heures_budgetees_approbation',
    'heures_budgetees_historique_approbations',
  ];
  const cleaned = { ...payload };
  for (const key of MASKED_FIELDS) {
    if (cleaned[key] === null) {
      delete cleaned[key];
    }
  }
  return cleaned;
}
